import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Phone, Leaf } from 'lucide-react';
import { Button } from './ui/button';
import { FALL_SPECIAL, fallPromoCopy } from '../data/fallSpecial';
import { trackEvent } from '../lib/analytics';

// Offer + terms (terms always directly under the offer) + the two CTAs. Used by the popup, homepage section and /fall-ready.
export const FallOfferBlock = ({ source, dark = false, compact = false, onNavigate }) => {
  const navigate = useNavigate();
  const copy = fallPromoCopy();
  const goEstimate = () => {
    trackEvent('generate_lead', { source, promo: FALL_SPECIAL.key, active: copy.active });
    onNavigate?.();
    navigate(copy.active ? FALL_SPECIAL.bookingPath : '/booking');
  };
  const callNow = () => { trackEvent('click_to_call', { source, promo: FALL_SPECIAL.key }); window.location.href = FALL_SPECIAL.phoneTel; };
  const text = dark ? 'text-white' : 'text-slate-900';
  const fine = dark ? FALL_SPECIAL.theme.fine : 'text-slate-600';
  return (
    <div className={`${compact ? 'space-y-2' : 'space-y-3'}`} data-testid="fall-offer-block">
      {copy.active ? (
        <>
          <p className={`${compact ? 'text-base' : 'text-lg sm:text-xl'} font-extrabold leading-snug ${text}`} data-testid="fall-offer-text">{copy.offer}</p>
          <p className={`text-xs sm:text-sm leading-relaxed ${fine}`} data-testid="fall-offer-terms">{copy.terms}</p>
        </>
      ) : (
        <p className={`${compact ? 'text-base' : 'text-lg sm:text-xl'} font-extrabold leading-snug ${text}`} data-testid="fall-offer-text">{FALL_SPECIAL.expired.bannerText}</p>
      )}
      <div className={`flex flex-col sm:flex-row gap-2 ${compact ? '' : 'sm:gap-3 pt-1'}`}>
        <Button onClick={goEstimate} size={compact ? 'default' : 'lg'} className="bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md flex-1" data-testid="fall-get-estimate-btn">
          <Calendar className="w-4 h-4 mr-2" /> {copy.ctaText}
        </Button>
        <Button onClick={callNow} size={compact ? 'default' : 'lg'} variant="outline" className={`flex-1 font-semibold ${dark ? 'border-white/60 bg-white/10 text-white hover:bg-white/20' : 'border-orange-600 text-orange-700 hover:bg-orange-50'}`} data-testid="fall-call-btn">
          <Phone className="w-4 h-4 mr-2" /> Call {FALL_SPECIAL.phoneDisplay}
        </Button>
      </div>
      {copy.active && !compact && (
        <p className={`text-xs inline-flex items-center gap-1.5 ${fine}`}><Leaf className="w-3.5 h-3.5" /> Mention “{FALL_SPECIAL.promoLabel}” when requesting your estimate — the online form adds it for you.</p>
      )}
    </div>
  );
};

export const FallServiceGroups = ({ dark = false, withPhotos = true }) => (
  <div className="grid sm:grid-cols-3 gap-3 sm:gap-4" data-testid="fall-service-groups">
    {FALL_SPECIAL.groups.map((g) => (
      <div key={g.key} className={`rounded-xl overflow-hidden border ${dark ? 'border-white/15 bg-white/10' : `${FALL_SPECIAL.theme.card} bg-white shadow-sm`}`} data-testid={`fall-group-${g.key}`}>
        {withPhotos && <img src={g.photo} alt={`${g.title} — A Quality Handyman project photo`} className="w-full h-28 sm:h-32 object-cover" loading="lazy" />}
        <div className="p-3 sm:p-4">
          <h3 className={`font-bold text-sm sm:text-base ${dark ? 'text-white' : 'text-slate-900'}`}>{g.title}</h3>
          <p className={`text-xs sm:text-sm mt-1 leading-relaxed ${dark ? FALL_SPECIAL.theme.soft : 'text-slate-600'}`}>{g.items}</p>
        </div>
      </div>
    ))}
  </div>
);
